<script>
import FileBrowser from '@/components/file-browser/FileBrowser';
import Panel from '@/components/ui/Panel';
import URI from 'urijs';
import { dirname, join, removeBasePath } from '@/modules/file-utilities';

export default {
  name: 'MetadataPanel',
  components: {
    FileBrowser,
    Panel
  },
  props: {
    page: {
      type: Object,
      required: true
    },
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      title: '',
      description: '',
      image: '',
      isImageBrowserOpen: false,
      robots: false
    };
  },
  methods: {
    show(data) {
      this.title = data.title || '';
      this.description = data.description || '';
      this.image = data.image || '';
      this.robots = data.robots === true ? true : false;
      this.$refs.panel.show();
    },

    handleShow() {
      this.$refs.title.focus();
    },

    hide() {
      this.$refs.panel.hide();
      this.title = '';
      this.description = '';
      this.image = '';
      this.robots = false;
    },

    handleSubmit() {
      const metadata = {
        title: this.title,
        description: this.description,
        image: this.image,
        robots: this.robots
      };
      this.$refs.panel.hide();
      this.$emit('submit', metadata);
    },

    showImageBrowser() {
      let path = this.site.uploadFolder;

      // If the current image URL looks like a relative path, use that directory
      if (!this.image.match(/^https?:\/\//i)) {
        const fileUrl = new URI(this.image);
        path = join(this.site.homeFolder, dirname(fileUrl.path()));
      }

      this.isImageBrowserOpen = true;
      this.$refs.imageBrowser.open({ path });
    },

    hideImageBrowser() {
      this.$refs.imageBrowser.close();
      this.isImageBrowserOpen = false;
    },

    handleImageBrowserSelect(type, files) {
      if (files.length) {
        if (type === 'upload') {
          const file = files[0];
          const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
          const fileUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, removeBasePath(file.path, this.site.homeFolder)));
          const image = fileUrl.absoluteTo(pageUrl).toString(); // must be absolute for Open Graph

          this.image = image;
          this.$refs.imageBrowserPopover.doClose();
        } else if (type === 'unsplash') {
          const image = files[0].urls.regular;

          this.image = image;
          this.$refs.imageBrowserPopover.doClose();
        }
      }
    },

    handleImageChange() {
      // Image URL must be absolute for Open Graph
      if (this.image.length && !this.image.match(/^https?:/i)) {
        const pageUrl = new URI(this.site.url).path(URI.joinPaths(this.site.url, this.page.path));
        const imageUrl = new URI(this.image);
        this.image = imageUrl.absoluteTo(pageUrl).toString();
      }
    }
  }
};
</script>

<template>
  <panel
    ref="panel"
    :title="$t('Metadata')"
    :close-on-click="!isImageBrowserOpen"
    @show="handleShow"
  >
    <el-form
      label-position="top"
      novalidate
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <!-- Title -->
        <el-form-item :label="$t('Title')">
          <el-input
            ref="title"
            v-model="title"
            type="text"
            auto-complete="off"
          />
          <div class="text-form-description">
            {{ $t('Shown in browser tabs and search results.') }}
          </div>
        </el-form-item>

        <!-- Description -->
        <el-form-item :label="$t('Description')">
          <el-input
            v-model="description"
            type="textarea"
            auto-complete="off"
          />
          <div class="text-form-description">
            {{ $t('Shown under the title in search results.') }}
          </div>
        </el-form-item>

        <!-- Image -->
        <el-form-item :label="$t('Image')">
          <el-input
            ref="image"
            v-model="image"
            type="url"
            clearable
            prefix-icon="el-icon-fa-image"
            auto-complete="off"
            spellcheck="false"
            @blur="handleImageChange"
          >
            <template slot="append">
              <el-popover
                ref="imageBrowserPopover"
                placement="bottom"
                trigger="click"
                @show="showImageBrowser()"
                @hide="hideImageBrowser()"
              >
                <el-button slot="reference">
                  {{ $t('Browse') }}
                </el-button>

                <file-browser
                  ref="imageBrowser"
                  :base-path="site.uploadFolder"
                  :can-select="file => file.type === 'file' && file.name.match(/\.(gif|jpeg|jpg|png|svg)$/i)"
                  :multiple="false"
                  :site="site"
                  :show-unsplash="true"
                  type="popover"
                  @select="handleImageBrowserSelect"
                />
              </el-popover>
            </template>
          </el-input>
          <div class="text-form-description">
            {{ $t('Shown when the page is shared in third-party apps.') }}
          </div>
        </el-form-item>

        <!-- Robots -->
        <el-form-item>
          <el-switch
            v-model="robots"
            :active-text="$t('Prevent Search Engines From Indexing This Page')"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-button type="primary" native-type="submit">
              {{ $t('Update Metadata') }}
            </el-button>
          </el-col>
        </el-row>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

//
// No styles yet
//

</style>
